import { useState } from 'react'
import { Users, Building2, Settings2, ClipboardList } from 'lucide-react'

const tabs = [
  { id: 'organization', name: '組織図', icon: Users },
  { id: 'profile', name: 'プロファイル', icon: Building2 },
  { id: 'templates', name: 'テンプレート', icon: Settings2 },
  { id: 'audit', name: '監査ログ', icon: ClipboardList },
]

const organization = {
  name: '株式会社サンプル',
  departments: [
    {
      name: '経営',
      members: [
        { name: '代表取締役 山田太郎', role: 'CEO' },
        { name: '取締役 佐藤花子', role: 'Director' },
      ],
    },
    {
      name: '営業部',
      members: [
        { name: '部長 田中一郎', role: 'Manager' },
      ],
      children: [
        {
          name: '営業1課',
          members: [
            { name: '課長 鈴木二郎', role: 'Leader' },
            { name: '主任 高橋三郎', role: 'Senior' },
          ],
        },
        {
          name: '営業2課',
          members: [],
        },
      ],
    },
    {
      name: '開発部',
      members: [],
    },
  ],
}

const profiles = [
  { id: 'sales', name: '営業会社プロファイル', selected: true },
  { id: 'construction', name: '建設会社プロファイル', selected: false },
  { id: 'it', name: 'IT企業プロファイル', selected: false },
  { id: 'general', name: '汎用プロファイル', selected: false },
]

const analysisFocuses = [
  { id: 'deal_progress', name: '商談進捗', enabled: true },
  { id: 'customer_sentiment', name: '顧客感情', enabled: true },
  { id: 'competitor', name: '競合情報', enabled: true },
  { id: 'next_action', name: '次回アクション', enabled: true },
  { id: 'schedule', name: '工程管理', enabled: false, note: '建設向け' },
]

export function AdminPage() {
  const [activeTab, setActiveTab] = useState('organization')

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">管理者設定</h1>
        <p className="text-muted-foreground">
          組織・プロファイル・テンプレートの管理
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.name}
          </button>
        ))}
      </div>

      {/* Organization tab */}
      {activeTab === 'organization' && (
        <div className="rounded-lg border bg-card shadow-sm">
          <div className="flex items-center justify-between border-b px-6 py-4">
            <h2 className="font-semibold">組織図</h2>
            <button className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90">
              + メンバー追加
            </button>
          </div>
          <div className="p-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                <span className="font-medium">{organization.name}</span>
              </div>
              <div className="ml-6 space-y-2">
                {organization.departments.map((dept, deptIndex) => (
                  <div key={deptIndex} className="space-y-1">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <span>├──</span>
                      <span className="text-foreground">📁 {dept.name}</span>
                    </div>
                    <div className="ml-8 space-y-1">
                      {dept.members.map((member, memberIndex) => (
                        <div key={memberIndex} className="flex items-center gap-2 text-sm">
                          <span className="text-muted-foreground">├──</span>
                          <span>👤 {member.name}</span>
                        </div>
                      ))}
                      {dept.children?.map((child, childIndex) => (
                        <div key={childIndex} className="space-y-1">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <span>├──</span>
                            <span className="text-foreground">📁 {child.name}</span>
                          </div>
                          <div className="ml-8 space-y-1">
                            {child.members.map((member, memberIndex) => (
                              <div key={memberIndex} className="flex items-center gap-2 text-sm">
                                <span className="text-muted-foreground">├──</span>
                                <span>👤 {member.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile tab */}
      {activeTab === 'profile' && (
        <div className="space-y-6">
          <div className="rounded-lg border bg-card p-6 shadow-sm">
            <h2 className="mb-4 font-semibold">業種プロファイル</h2>
            <div>
              <label className="text-sm font-medium">現在のプロファイル</label>
              <select className="mt-1 w-full rounded-md border bg-background px-3 py-2">
                {profiles.map((profile) => (
                  <option key={profile.id} value={profile.id} selected={profile.selected}>
                    {profile.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="rounded-lg border bg-card p-6 shadow-sm">
            <h2 className="mb-4 font-semibold">有効な分析フォーカス</h2>
            <div className="space-y-3">
              {analysisFocuses.map((focus) => (
                <div key={focus.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={focus.id}
                      defaultChecked={focus.enabled}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <label htmlFor={focus.id} className="text-sm">
                      {focus.name}（{focus.id}）
                    </label>
                    {focus.note && (
                      <span className="text-xs text-muted-foreground">
                        ← {focus.note}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <button className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90">
                カスタマイズ
              </button>
              <button className="rounded-md border px-4 py-2 text-sm hover:bg-muted">
                リセット
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Templates tab */}
      {activeTab === 'templates' && (
        <div className="rounded-lg border bg-card p-6 shadow-sm">
          <h2 className="mb-4 font-semibold">提案テンプレート</h2>
          <p className="text-muted-foreground">
            提案テンプレートの管理機能は Phase 3 で実装予定です。
          </p>
        </div>
      )}

      {/* Audit tab */}
      {activeTab === 'audit' && (
        <div className="rounded-lg border bg-card p-6 shadow-sm">
          <h2 className="mb-4 font-semibold">監査ログ</h2>
          <p className="text-muted-foreground">
            監査ログ機能は Phase 2 で実装予定です。
          </p>
        </div>
      )}
    </div>
  )
}
